import { useState, useRef, useCallback, useEffect } from 'react';
import { formatCurrency, multiplyMoney, sumMoney } from '@peles/shared';
import { X, Upload, Loader2, CheckCircle, AlertTriangle, FileText } from 'lucide-react';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useAnalyzeQuote, useConfirmQuoteImport } from '../../hooks/useQuoteImport';
import { useVendorCategories } from '../../hooks/useVendors';
import { documentFileProblem, uploadAndRead } from './documentReader';
import type { QuoteAnalysisResult, QuoteImportItem } from '../../api/quote-import';

/**
 * What the modal says. It imports invoices: a quote is read into that vendor's
 * quote on its need, never into an expense line.
 */
const wording = {
  title: 'Import from Invoice',
  noun: 'invoice',
  analyze: 'Analyze Invoice',
  testIdPrefix: 'invoice',
};

type Step = 'upload' | 'analyzing' | 'review';

interface DocumentImportModalProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

interface ReviewItem extends QuoteImportItem {
  index: number;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const percent = Math.round(confidence * 100);
  if (confidence >= 0.8) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
        <CheckCircle size={12} />
        High confidence ({percent}%)
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
        confidence >= 0.5 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
      }`}
    >
      <AlertTriangle size={12} />
      {confidence >= 0.5 ? 'Medium' : 'Low'} confidence ({percent}%)
    </span>
  );
}

const inputClass =
  'w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Upload a vendor's invoice, let the AI read its lines, review them, and
 * import the ones kept as booked expense lines. Reading the file is
 * `uploadAndRead`, which a quote's "read from the file" shares.
 */
export function DocumentImportModal({
  eventId,
  isOpen,
  onClose,
  onImported,
}: DocumentImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [documentId, setDocumentId] = useState('');
  const [analysis, setAnalysis] = useState<QuoteAnalysisResult | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [importError, setImportError] = useState('');

  const analyze = useAnalyzeQuote();
  const confirmImport = useConfirmQuoteImport();
  const { data: categories = [] } = useVendorCategories();

  useEffect(() => {
    if (isOpen) return;
    setStep('upload');
    setDragActive(false);
    setUploadError('');
    setSelectedFile(null);
    setIsUploading(false);
    setDocumentId('');
    setAnalysis(null);
    setVendorName('');
    setReviewItems([]);
    setImportError('');
    analyze.reset();
    confirmImport.reset();
  }, [isOpen]);

  const handleFileSelected = useCallback((file: File) => {
    const problem = documentFileProblem(file);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setUploadError('');
    setSelectedFile(file);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!selectedFile) return;
    setStep('analyzing');
    setUploadError('');
    try {
      setIsUploading(true);
      const result = await uploadAndRead(selectedFile, eventId, (request) => {
        setIsUploading(false);
        return analyze.mutateAsync(request);
      });
      setDocumentId(result.documentId);
      setAnalysis(result.analysis);
      setVendorName(result.analysis.vendorName || '');
      setReviewItems(
        result.analysis.lineItems.map((item, index) => ({ ...item, accepted: true, index })),
      );
      setStep('review');
    } catch (err) {
      setIsUploading(false);
      setStep('upload');
      setUploadError(
        err instanceof Error ? err.message : `Failed to analyze ${wording.noun}. Please try again.`,
      );
    }
  }, [selectedFile, eventId, analyze]);

  const toggleItem = (index: number) =>
    setReviewItems((prev) =>
      prev.map((item) => (item.index === index ? { ...item, accepted: !item.accepted } : item)),
    );

  const allAccepted = reviewItems.length > 0 && reviewItems.every((i) => i.accepted);
  const toggleAll = () =>
    setReviewItems((prev) => prev.map((item) => ({ ...item, accepted: !allAccepted })));

  const changeItem = (index: number, patch: Partial<ReviewItem>) =>
    setReviewItems((prev) =>
      prev.map((item) => {
        if (item.index !== index) return item;
        const updated = { ...item, ...patch };
        if ('quantity' in patch || 'unitPrice' in patch) {
          updated.totalPrice = multiplyMoney(updated.quantity, updated.unitPrice);
        }
        return updated;
      }),
    );

  const accepted = reviewItems.filter((item) => item.accepted);

  const handleImport = useCallback(async () => {
    setImportError('');
    if (accepted.length === 0) {
      setImportError('Please select at least one item to import.');
      return;
    }
    try {
      await confirmImport.mutateAsync({
        eventId,
        documentId,
        vendorName: vendorName || undefined,
        items: accepted.map(({ index: _index, ...rest }) => rest),
      });
      onImported();
      onClose();
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : 'Failed to import items. Please try again.',
      );
    }
  }, [accepted, eventId, documentId, vendorName, confirmImport, onImported, onClose]);

  const selectedTotal = sumMoney(accepted.map((item) => item.totalPrice));

  const footer = (
    <div className="flex w-full items-center justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        className="min-h-11 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
      >
        Cancel
      </button>
      {step === 'upload' && (
        <button
          type="button"
          onClick={handleAnalyze}
          disabled={!selectedFile}
          className="min-h-11 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {wording.analyze}
        </button>
      )}
      {step === 'review' && (
        <button
          type="button"
          onClick={handleImport}
          disabled={confirmImport.isPending || accepted.length === 0}
          className="min-h-11 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {confirmImport.isPending ? 'Importing...' : `Import Selected (${accepted.length})`}
        </button>
      )}
    </div>
  );

  return (
    <EntityModal
      open={isOpen}
      onClose={onClose}
      title={wording.title}
      size="lg"
      footer={footer}
      closeOnBackdrop={step !== 'review'}
    >
      {step === 'upload' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Upload a vendor {wording.noun} (PDF or image) and our AI will extract the line items for
            you to review and import into your expense tracker.
          </p>

          <div
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              if (e.dataTransfer.files.length > 0) handleFileSelected(e.dataTransfer.files[0]);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
            }`}
            data-testid={`${wording.testIdPrefix}-dropzone`}
          >
            <Upload className="mx-auto mb-3 text-gray-400" size={32} />
            <p className="text-sm text-gray-600">
              Drag & drop your {wording.noun} here, or{' '}
              <span className="text-primary-600 font-medium">browse</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">PDF, JPEG, PNG, WebP, TIFF up to 25 MB</p>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf,image/jpeg,image/png,image/webp,image/tiff"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFileSelected(e.target.files[0]);
                  e.target.value = '';
                }
              }}
              className="hidden"
              data-testid={`${wording.testIdPrefix}-file-input`}
            />
          </div>

          {selectedFile && (
            <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg">
              <FileText size={20} className="text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedFile(null)}
                className="min-h-11 min-w-11 flex items-center justify-center text-gray-400 hover:text-gray-600"
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
        </div>
      )}

      {step === 'analyzing' && (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 size={40} className="text-primary-600 animate-spin" />
          <p className="text-sm text-gray-600">
            {isUploading ? 'Uploading document...' : `Analyzing your ${wording.noun} with AI...`}
          </p>
          <p className="text-xs text-gray-400">This may take a few moments</p>
        </div>
      )}

      {step === 'review' && analysis && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Extracted Data</h3>
            <ConfidenceBadge confidence={analysis.confidence} />
          </div>

          <div>
            <label
              htmlFor={`${wording.testIdPrefix}-vendor-name`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Vendor Name
            </label>
            <input
              id={`${wording.testIdPrefix}-vendor-name`}
              type="text"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              placeholder="Vendor name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {(analysis.paymentTerms || analysis.dueDate) && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              {analysis.paymentTerms && (
                <span>
                  <span className="font-medium text-gray-700">Payment Terms:</span>{' '}
                  {analysis.paymentTerms}
                </span>
              )}
              {analysis.dueDate && (
                <span>
                  <span className="font-medium text-gray-700">Due Date:</span> {analysis.dueDate}
                </span>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">
                Line Items ({accepted.length} of {reviewItems.length} selected)
              </h4>
              <button
                type="button"
                onClick={toggleAll}
                className="min-h-11 px-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                {allAccepted ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* One card per line: a grid on wide screens, stacked on a phone. */}
            <ul className="space-y-3">
              {reviewItems.map((item) => (
                <li
                  key={item.index}
                  className={`border border-gray-200 rounded-lg p-3 ${
                    item.accepted ? 'bg-white' : 'bg-gray-50 opacity-60'
                  }`}
                  data-testid="import-line"
                >
                  <label className="flex items-start gap-3 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={item.accepted}
                      onChange={() => toggleItem(item.index)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-900 font-medium flex-1">
                      {item.description}
                    </span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:ml-7">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Qty
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            changeItem(item.index, { quantity: parseFloat(e.target.value) || 0 })
                          }
                          min="0"
                          step="1"
                          className={`${inputClass} mt-1 text-right`}
                        />
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Unit Price
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) =>
                            changeItem(item.index, { unitPrice: parseFloat(e.target.value) || 0 })
                          }
                          min="0"
                          step="0.01"
                          className={`${inputClass} mt-1 text-right`}
                        />
                      </label>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500 mb-1">Total</span>
                      <p className="text-sm font-medium text-gray-900 py-1.5 mt-1">
                        {formatCurrency(item.totalPrice)}
                      </p>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500 mb-1">Category</span>
                      <div className="mt-1">
                        <SearchableListSelect
                          items={categories}
                          value={item.vendorCategoryId ?? null}
                          onChange={(value) =>
                            changeItem(item.index, {
                              vendorCategoryId: (value as string) || undefined,
                            })
                          }
                          getValue={(cat) => cat.id}
                          getLabel={(cat) => cat.name}
                          placeholder="Select..."
                          ariaLabel={`Category for ${item.description}`}
                          clearable
                        />
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Selected Total</span>
            <span className="text-lg font-semibold text-gray-900">
              {formatCurrency(selectedTotal)}
            </span>
          </div>

          {importError && <p className="text-sm text-red-600">{importError}</p>}
        </div>
      )}
    </EntityModal>
  );
}
